import type { FundingApplicationSchemas } from "api-types";

import type { FormattedFundingApplicationScreen } from "../../../queries/funding-application/queries";
import { isEcommerceConnectorSelectedLegacy, isPaymentsConnectorSelectedLegacy } from "../../../utils/connectors";
import { MARKETING_CONNECTOR_SLUGS, REVENUE_CONNECTOR_SLUGS } from "../../../components/constants";

export const canPlatformBeRemoved = (
  platformToRemove: string,
  fundingApplication: FormattedFundingApplicationScreen
): boolean => {
  const updatedSlugs = fundingApplication.selectedConnectorSlugs.filter((platform) => {
    return platform !== platformToRemove;
  });
  const hasEcommercePlatform = isEcommerceConnectorSelectedLegacy(updatedSlugs);
  const hasPaymentPlatform = isPaymentsConnectorSelectedLegacy(updatedSlugs);
  if (
    hasEcommercePlatform ||
    updatedSlugs.includes("ga") ||
    hasPaymentPlatform ||
    fundingApplication.retailPercentage === "over_eighty"
  ) {
    return true;
  } else {
    return false;
  }
};

// Config flow only
const canRemoveRevenuePlatform = (
  platformToRemove: string,
  updatedSlugs: string[],
  fundingApplicationConfig: FundingApplicationSchemas.CustomerFundingApplicationStagesScreen
) => {
  if (!fundingApplicationConfig.revenue) {
    // No revenue stage, so no requirements - we can remove this platform
    return true;
  }
  if (fundingApplicationConfig.revenue.requiredConnectorSlugs?.includes(platformToRemove)) {
    // This platform slug is required, so we can't remove it
    return false;
  }
  if (fundingApplicationConfig.revenue.requiredConnectorSlugs?.some((slug) => REVENUE_CONNECTOR_SLUGS.includes(slug))) {
    // The required slugs includes a revenue platform then we can remove the selected platform
    return true;
  }
  if (
    fundingApplicationConfig.revenue.platformRequired &&
    !updatedSlugs.some((slug) => REVENUE_CONNECTOR_SLUGS.includes(slug))
  ) {
    // Revenue platform is required and updated list doesn't have one, so we can't remove it
    return false;
  }
  return true;
};

// Config flow only
const canRemoveMarketingPlatform = (
  platformToRemove: string,
  updatedSlugs: string[],
  fundingApplicationConfig: FundingApplicationSchemas.CustomerFundingApplicationStagesScreen
) => {
  if (!fundingApplicationConfig.marketing) {
    // No marketing stage, so no requirements - we can remove this platform
    return true;
  }
  if (fundingApplicationConfig.marketing.requiredConnectorSlugs?.includes(platformToRemove)) {
    // This platform slug is required, so we can't remove it
    return false;
  }
  if (
    fundingApplicationConfig.marketing.requiredConnectorSlugs?.some((slug) => MARKETING_CONNECTOR_SLUGS.includes(slug))
  ) {
    // The required slugs includes a marketing platform then we can remove the selected platform
    return true;
  }
  if (
    fundingApplicationConfig.marketing.platformRequired &&
    !updatedSlugs.some((slug) => MARKETING_CONNECTOR_SLUGS.includes(slug))
  ) {
    // Marketing platform is required and updated list doesn't have one, so we can't remove it
    return false;
  }
  return true;
};

export const canPlatformBeRemovedNext = (
  platformToRemove: string,
  fundingApplication: FormattedFundingApplicationScreen,
  fundingApplicationStages: FundingApplicationSchemas.CustomerFundingApplicationStagesScreen
): boolean => {
  const updatedSlugs = fundingApplication.selectedConnectorSlugs.filter((platform) => {
    return platform !== platformToRemove;
  });
  if (REVENUE_CONNECTOR_SLUGS.includes(platformToRemove)) {
    return canRemoveRevenuePlatform(platformToRemove, updatedSlugs, fundingApplicationStages);
  }
  if (MARKETING_CONNECTOR_SLUGS.includes(platformToRemove)) {
    return canRemoveMarketingPlatform(platformToRemove, updatedSlugs, fundingApplicationStages);
  }
  return true;
};
